import { useContext, useEffect, useState } from 'react';
import { IIconV2WorldMarker } from 'react-give-me-map';
import { CtxEdit } from 'session/ctxEdit';

import { useDirectMarkerUpdates } from 'components/minute/builders/v2/useDirectMarkerUpdates';
import { MovePoint } from 'components/site/general/singleSelect/SelectedMarkerV2/MovePoint';
import { SelectArea } from 'components/site/general/singleSelect/SelectedMarkerV2/SelectedArea';
import 'components/site/general/singleSelect/SelectedMarkerV2/style.css';
import { ICoordinates } from 'libs/types/map/mapTypes';
import { IXY } from 'react-give-me-map/dist/utils/map/mapTypes';
import {
    IIndoorStandWorldMarker,
    IVariantIconV2WorldMarker
} from 'react-give-me-map/dist/utils/world/worldTypes';
import { useUpdateEffect } from 'usehooks-ts';

interface VariantIconProps {
    markerId: string;
    type: 'v2/variant_icon';
    allMarkersOfType: IVariantIconV2WorldMarker[];
}

interface IconIndoorStandProps {
    markerId: string;
    type: 'v2/icon' | 'indoor_stand';
    allMarkersOfType: (IIconV2WorldMarker | IIndoorStandWorldMarker)[];
}

type Props = VariantIconProps | IconIndoorStandProps;

export const SelectedMarkerIconCommon = (props: Props) => {
    const ctxEdit = useContext(CtxEdit);
    const directMarkerUpdates = useDirectMarkerUpdates();

    // Same logic is executed, but this fixes the type issue with typescript
    const marker =
        props.type === 'v2/variant_icon'
            ? props.allMarkersOfType.find((e) => e.id === props.markerId)
            : props.allMarkersOfType.find((e) => e.id === props.markerId);

    function getInitialXyCoordinate(): IXY {
        return ctxEdit.mapRef.current?.project([marker.lng, marker.lat]);
    }
    const [xyCoordinate, setXyCoordinate] = useState(() => getInitialXyCoordinate());
    useEffect(() => {
        const listener = () => {
            setXyCoordinate(getInitialXyCoordinate());
        };
        ctxEdit.mapRef.current?.on('move', listener);
        return () => {
            ctxEdit.mapRef.current?.off('move', listener);
        };
    });
    useUpdateEffect(() => {
        setXyCoordinate(getInitialXyCoordinate());
    }, [marker.lat, marker.lng]);

    const updateMarkerDirectly = (newCoordinates: ICoordinates) => {
        if (props.type === 'v2/variant_icon') {
            directMarkerUpdates.updateVariantIconPosition(marker.id, newCoordinates);
        } else {
            const newMarkers = props.allMarkersOfType.map((e) =>
                e.id === marker.id ? { ...e, lat: newCoordinates.lat, lng: newCoordinates.lng } : e
            );
            directMarkerUpdates.updateIconLikeMarkers(props.type, newMarkers);
        }
    };

    const updateMarkerGlobally = (newCoordinates: ICoordinates) => {
        ctxEdit.updateWorldMarker(marker.id, { lat: newCoordinates.lat, lng: newCoordinates.lng });
    };

    if (marker.isExternalPoint) {
        return null;
    }

    return (
        <SelectArea>
            <MovePoint
                xyCoordinates={[xyCoordinate]}
                onDrag={(diffX, diffY) => {
                    const point = { x: xyCoordinate.x + diffX, y: xyCoordinate.y + diffY };
                    const latLng = ctxEdit.mapRef.current?.unproject([point.x, point.y]);
                    updateMarkerDirectly({ lat: latLng.lat, lng: latLng.lng });
                }}
                onDragEnd={(diffX, diffY) => {
                    const point = { x: xyCoordinate.x + diffX, y: xyCoordinate.y + diffY };
                    const latLng = ctxEdit.mapRef.current?.unproject([point.x, point.y]);
                    updateMarkerGlobally({ lat: latLng.lat, lng: latLng.lng });
                }}
            />
        </SelectArea>
    );
};
